import { CapabilityBitFlags, telSysCapabilityBitFlags } from '../../CMEAddModify/Constants'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { TelSysConfigurationModalID, TelSysTypes } from '../Constants/Constants'

const CXI_API_SUPPORTED_CAPABILITIES: CapabilityBitFlags[] = [
    CapabilityBitFlags.CXI_BROADWORKS,
    CapabilityBitFlags.CXI_WEBEX,
    CapabilityBitFlags.CXI_TEAMS
]

export type CxiApiTabSwitchers = {
    defaultSiteSettingsEnabled: boolean
    advancedEnabled: boolean
}

const API_ENABLED_TELSYS_TYPES: TelSysTypes[] = [
    TelSysTypes.BROADSOFT_BROADWORKS,
    TelSysTypes.WEBEX_CALLING,
    TelSysTypes.MS_TEAMS,
    TelSysTypes.CXI_API
]

/**
 * Returns whether the selected TelSys type is the API-only CXI type.
 *
 * @param telSysType The selected telephony server type.
 * @returns True when the type is CXI API.
 */
export const isCxiApiTelSysType = (telSysType?: TelSysTypes): boolean => telSysType === TelSysTypes.CXI_API

/**
 * Returns whether the selected TelSys type can expose CXI API controls.
 *
 * @param telSysType The selected telephony server type.
 * @returns True when the TelSys type supports the API toggle.
 */
export const isApiConfigurableTelSysType = (telSysType?: TelSysTypes): boolean =>
    !!telSysType && API_ENABLED_TELSYS_TYPES.includes(telSysType)

/**
 * Returns whether the current CME can create a CXI API TelSys.
 *
 * @param cmeCapabilities Capability bitmask for the current CME.
 * @returns True when any CX Integration capability is enabled.
 */
export const isCxiApiTelSysSupported = (cmeCapabilities: number): boolean =>
    CXI_API_SUPPORTED_CAPABILITIES.some(
        (capability: CapabilityBitFlags): boolean => (cmeCapabilities & capability) !== 0
    )

/**
 * Returns whether the current CME has the CX Integration capability enabled for the given TelSys type.
 *
 * <p>Unlike {@link isCxiApiTelSysSupported}, this checks the specific capability bit that maps to the
 * selected integrated TelSys type (Teams, Webex, BroadWorks) rather than any CXI capability.
 *
 * @param telSysType The selected telephony server type.
 * @param cmeCapabilities Capability bitmask for the current CME.
 * @returns True when the matching CX Integration capability bit is set for the type.
 */
export const isCxiCapableForTelSysType = (telSysType: TelSysTypes | undefined, cmeCapabilities: number): boolean => {
    const capability: CapabilityBitFlags | undefined = telSysType ? telSysCapabilityBitFlags[telSysType] : undefined

    return capability ? isBitmaskMatching(cmeCapabilities, capability) : false
}

/**
 * Returns whether the CXI API controls (API Enabled checkbox etc.) should be shown for the selected type.
 *
 * <p>For the standalone CXI API type the controls depend only on the CME supporting any CX Integration
 * capability. For integrated types (Teams, Webex, BroadWorks) the CME must have the matching CX Integration
 * capability enabled — otherwise the controls stay hidden.
 *
 * @param telSysType The selected telephony server type.
 * @param cmeCapabilities Capability bitmask for the current CME.
 * @returns True when the API controls should be rendered.
 */
export const shouldShowApiControls = (telSysType: TelSysTypes | undefined, cmeCapabilities: number): boolean => {
    if (!isApiConfigurableTelSysType(telSysType)) return false

    if (isCxiApiTelSysType(telSysType)) return isCxiApiTelSysSupported(cmeCapabilities)

    return isCxiCapableForTelSysType(telSysType, cmeCapabilities)
}

/**
 * Returns whether the selected tab should remain visible but disabled for the CXI API type.
 *
 * @param telSysType Selected telephony server type.
 * @param tabID The current tab identifier.
 * @param tabSwitchers Switches that control whether optional tabs remain enabled.
 * @returns True when the tab should be visible but disabled.
 */
export const isDisabledForCxiApiTelSys = (
    telSysType: TelSysTypes | undefined,
    tabID: number,
    tabSwitchers: CxiApiTabSwitchers
): boolean =>
    isCxiApiTelSysType(telSysType) &&
    ((tabID === TelSysConfigurationModalID.TELSYS_DEFAULT_SITE_SETTINGS && !tabSwitchers.defaultSiteSettingsEnabled) ||
        (tabID === TelSysConfigurationModalID.TELSYS_ADVANCED && !tabSwitchers.advancedEnabled))
